import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { ListIcon, XIcon } from "@phosphor-icons/react";
import BarMark from "@/components/landing/BarMark";

const navLinks = [
  { href: "#problem", label: "Problem" },
  { href: "#product", label: "What Ranq does" },
  { href: "#process", label: "Early access" },
  { href: "#confidentiality", label: "Confidentiality" },
  { href: "#faq", label: "FAQ" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollToForm = () => {
    setOpen(false);
    const el = document.getElementById("early-access");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      data-testid="ranq-navbar"
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-[#f3efe6]/85 border-b border-stone-200/70"
          : "backdrop-blur-md bg-[#f3efe6]/40 border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
        <a
          href="#top"
          data-testid="nav-logo"
          aria-label="Ranq — Legal Intelligence"
          className="flex items-center gap-3 group"
        >
          <span className="ranq-wordmark text-[28px] text-stone-900">ranq</span>
          <BarMark size={20} variant="solid" className="text-stone-900 -ml-0.5" />
          <span className="hidden md:inline-flex items-center pl-3 ml-1 ranq-lockup-rule">
            <span className="pl-3 text-[10px] uppercase tracking-[0.22em] text-stone-600 font-medium leading-tight">
              Legal
              <br />
              Intelligence
            </span>
          </span>
        </a>

        <nav className="hidden lg:flex items-center gap-8">
          {navLinks.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="text-sm text-stone-600 hover:text-stone-900 transition-colors"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <Button
            data-testid="nav-request-access-btn"
            onClick={scrollToForm}
            className="hidden sm:inline-flex h-11 px-6 rounded-full bg-stone-900 hover:bg-stone-800 text-[#f7f4ec] font-medium"
          >
            Request early access
          </Button>
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            data-testid="nav-mobile-toggle"
            className="lg:hidden h-10 w-10 rounded-full border border-stone-300 bg-[#f7f4ec] flex items-center justify-center text-stone-900"
            aria-label="Toggle menu"
          >
            {open ? <XIcon size={18} /> : <ListIcon size={18} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-stone-200/70 bg-[#f7f4ec]/95 backdrop-blur-xl">
          <div className="max-w-7xl mx-auto px-6 py-4 flex flex-col gap-2">
            {navLinks.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                className="py-2 text-base text-stone-700 hover:text-stone-900"
              >
                {l.label}
              </a>
            ))}
            <Button
              data-testid="nav-mobile-cta"
              onClick={scrollToForm}
              className="mt-3 h-11 rounded-full bg-stone-900 hover:bg-stone-800 text-[#f7f4ec]"
            >
              Request early access
            </Button>
          </div>
        </div>
      )}
    </header>
  );
}
